// src/lib/progress-snapshot.js
import { User, DailyCheckIn, UserProgress } from "@/api/entities";
import { saveToR2 } from "@/api/r2";
import { add, format } from "date-fns";

// ——— Periodi standard degli obiettivi (come in Progress.jsx)
const PERIODI_OBIETTIVI = [
  { duration: { days: 1 }, label: "1 Giorno" },
  { duration: { days: 3 }, label: "3 Giorni" },
  { duration: { days: 7 }, label: "1 Settimana" },
  { duration: { days: 14 }, label: "2 Settimane" },
  { duration: { months: 1 }, label: "1 Mese" },
  { duration: { months: 2 }, label: "2 Mesi" },
  { duration: { months: 6 }, label: "6 Mesi" },
  { duration: { years: 1 }, label: "1 Anno" },
  { duration: { years: 2 }, label: "2 Anni" },
  { duration: { years: 5 }, label: "5 Anni" },
];

/* ===================== PATCH: utente sicuro ===================== */
function fallbackUserFromLocalStorage() {
  try {
    const p = JSON.parse(localStorage.getItem("RB_UserProfile") || "{}");
    if (p?.id || p?.email) {
      return { id: p.id ?? null, uid: p.id ?? null, email: p.email ?? null, ...p };
    }
  } catch {}
  return null;
}

async function getCurrentUserSafe() {
  let u = null;
  try { u = await User.me(); } catch {}
  if (!u || (!u.email && !u.id && !u.uid)) {
    const f = fallbackUserFromLocalStorage();
    if (f) u = f;
  }
  return u;
}
/* =============================================================== */

// ——— Normalizzatore check-in (compatibile con Progress.jsx)
function normCheckIn(ci) {
  if (!ci) return null;
  const dateSrc = ci.date || ci.day || ci.created_at || ci.now;
  const date = String(dateSrc || "").slice(0, 10); // YYYY-MM-DD
  const raw = ci.status || ci.q3_breathing || (ci.relapse ? "relapse" : "");
  const status = /relapse|fail|ricad/i.test(raw) ? "relapse" : /skip|none/i.test(raw) ? "skip" : "success";
  const cigarettes_avoided = Number(ci.cigarettes_avoided ?? ci.cigs_avoided ?? ci.cigs ?? 0) || 0;
  const savings_eur = Number(ci.savings_eur ?? ci.eur ?? ci.savings ?? 0) || 0;
  const note = ci.note || ci.notes || "";
  return { ...ci, date, status, q3_breathing: ci.q3_breathing ?? status, cigarettes_avoided, savings_eur, note };
}

// ——— Calcolo risparmi/sigarette evitate (stesso modello del Progress.jsx)
function computeSavings(user, checkIns) {
  if (!user?.smoking_type || checkIns.length === 0) {
    return { cigarettesAvoided: 0, totalSavings: 0 };
  }

  const sorted = [...checkIns].sort((a, b) => new Date(a.date) - new Date(b.date));
  const startDate = new Date(sorted[0].date + "T12:00:00");
  const today = new Date();

  let cigarettesAvoided = 0;
  let totalSavings = 0;

  const map = new Map(sorted.map((ci) => [ci.date, ci]));

  const CIGS_PER_TOBACCO_PACK = 100; // ✅ 1 pacco tabacco = 100 “sigarette” equivalenti

  for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
    const dateStr = format(d, "yyyy-MM-dd");
    const dayCheckIn = map.get(dateStr);
    const yearsPassed = d.getFullYear() - startDate.getFullYear();

    let dailyCigEquivalent = 0; // “sigarette equivalenti” di baseline giornaliere
    let dailyCost = 0;          // costo giornaliero baseline

    if (user.smoking_type === "cigarettes") {
      // fallback robusti sui campi
      const cpd = Number(user.cigarettes_per_day ?? user.baseline_cigs_per_day ?? 0);
      const packPriceBase = Number(user.pack_price ?? user.pack_price_eur ?? 0);
      const cigsPerPack = Number(user.cigarettes_per_pack ?? user.cigs_per_pack ?? 20);

      dailyCigEquivalent = cpd;
      const currentPackPrice = packPriceBase * Math.pow(1.03, yearsPassed);
      const pricePerCig = cigsPerPack > 0 ? currentPackPrice / cigsPerPack : 0;
      dailyCost = dailyCigEquivalent * pricePerCig;
    } else if (user.smoking_type === "tobacco") {
      // equivalenze + costi proporzionati su base giornaliera
      const weeklyTobaccoPacks = Number(user.tobacco_packs_per_week ?? 0);
      const weeklyPaperPacks   = Number(user.paper_packs_per_week ?? 0);
      const weeklyFilterPacks  = Number(user.filter_packs_per_week ?? 0);

      const tobaccoPackPriceBase = Number(user.tobacco_pack_price ?? 0);
      const paperPackPriceBase   = Number(user.paper_pack_price ?? 0);
      const filterPackPriceBase  = Number(user.filter_pack_price ?? 0);

      dailyCigEquivalent = (weeklyTobaccoPacks * CIGS_PER_TOBACCO_PACK) / 7;

      const dailyTobaccoCost = (weeklyTobaccoPacks * (tobaccoPackPriceBase * Math.pow(1.03, yearsPassed))) / 7;
      const dailyPaperCost   = (weeklyPaperPacks   * (paperPackPriceBase   * Math.pow(1.03, yearsPassed))) / 7;
      const dailyFilterCost  = (weeklyFilterPacks  * (filterPackPriceBase  * Math.pow(1.03, yearsPassed))) / 7;

      dailyCost = dailyTobaccoCost + dailyPaperCost + dailyFilterCost;
    }

    // Accendini: costo medio 1€ cad. (aggiustato per inflazione), ripartito su 7 giorni
    const weeklyLighters = Number(user.lighters_per_week ?? 0);
    const dailyLighterCost = (weeklyLighters * (1 * Math.pow(1.03, yearsPassed))) / 7;
    dailyCost += dailyLighterCost;

    // Ricadute del giorno (se presenti): numero di “sigarette equivalenti” fumate
    let smokedOnRelapse = 0;
    if (dayCheckIn && dayCheckIn.q3_breathing === "relapse") {
      smokedOnRelapse = Number(dayCheckIn.relapse_cigarettes_smoked ?? 0);
      if (!Number.isFinite(smokedOnRelapse) || smokedOnRelapse < 0) smokedOnRelapse = 0;
      // clamp per sicurezza a baseline del giorno
      if (dailyCigEquivalent > 0) smokedOnRelapse = Math.min(smokedOnRelapse, dailyCigEquivalent);
    }

    const pricePerCigEquivalent = dailyCigEquivalent > 0 ? (dailyCost / dailyCigEquivalent) : 0;
    const costOfRelapseCigs = smokedOnRelapse * pricePerCigEquivalent;

    cigarettesAvoided += Math.max(0, dailyCigEquivalent - smokedOnRelapse);
    totalSavings += Math.max(0, dailyCost - costOfRelapseCigs);
  }

  return {
    cigarettesAvoided: Math.round(cigarettesAvoided),
    totalSavings: Math.round(totalSavings),
  };
}

// ——— Achievements / Goal list / Streak (ridotto all’essenziale per lo snapshot)
function computeMilestones(checkIns, userProgress) {
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  const achievements = new Set();
  let upcomingGoals = [];
  let streak = 0;

  if (!checkIns.length) {
    // senza check-in: obiettivi da oggi
    PERIODI_OBIETTIVI.forEach((p) => upcomingGoals.push({ date: add(today, p.duration), label: p.label }));
    upcomingGoals = upcomingGoals.filter((g) => g.date > today).sort((a, b) => a.date - b.date);
    return { achievements: [], upcomingGoals, streakDays: 0 };
  }

  const sorted = [...checkIns].sort((a, b) => new Date(a.date) - new Date(b.date));
  const lastRelapseIndex = sorted.map((ci) => ci.q3_breathing === "relapse").lastIndexOf(true);

  let startDateForGoals;
  if (lastRelapseIndex === -1) {
    startDateForGoals = new Date(sorted[0].date + "T12:00:00");
  } else {
    const firstPositiveAfterRelapse = sorted.find((ci, idx) => idx > lastRelapseIndex && ci.q3_breathing !== "relapse");
    startDateForGoals = firstPositiveAfterRelapse ? new Date(firstPositiveAfterRelapse.date + "T12:00:00") : new Date(today);
  }

  // Obiettivi standard
  PERIODI_OBIETTIVI.forEach((p) => upcomingGoals.push({ date: add(startDateForGoals, p.duration), label: p.label }));

  // Streak & achievements base
  let maxStreak = 0;
  sorted.forEach((ci, idx) => {
    if (ci.q3_breathing === "relapse") streak = 0;
    else if (idx > lastRelapseIndex) streak++;
    else streak = 0;
    maxStreak = Math.max(maxStreak, streak);

    if (streak >= 1) achievements.add("1 Giorno");
    if (streak >= 3) achievements.add("3 Giorni");
    if (streak >= 7) achievements.add("1 Settimana");
    if (streak >= 14) achievements.add("2 Settimane");
    if (streak >= 30) achievements.add("1 Mese");
    if (streak >= 60) achievements.add("2 Mesi");
  });

  // Ricompense personalizzate dalla lezione 6
  const l6 = (userProgress || []).find((p) => p.exercise_completed && p.exercise_answer && p.lesson_id?.includes("6"));
  if (l6?.exercise_answer && typeof l6.exercise_answer === "string" && l6.exercise_answer.trim().startsWith("{")) {
    try {
      const rewards = JSON.parse(l6.exercise_answer);
      const rewardPeriods = {
        ricompensa_1_giorno:     { duration: { days: 1 },  label: "Ricompensa 1 giorno" },
        ricompensa_3_giorni:     { duration: { days: 3 },  label: "Ricompensa 3 giorni" },
        ricompensa_1_settimana:  { duration: { days: 7 },  label: "Ricompensa 1 settimana" },
        ricompensa_2_settimane:  { duration: { days: 14 }, label: "Ricompensa 2 settimane" },
        ricompensa_1_mese:       { duration: { months: 1 }, label: "Ricompensa 1 mese" },
        ricompensa_2_mesi:       { duration: { months: 2 }, label: "Ricompensa 2 mesi" },
        ricompensa_6_mesi:       { duration: { months: 6 }, label: "Ricompensa 6 mesi" },
        ricompensa_1_anno:       { duration: { years: 1 },  label: "Ricompensa 1 anno" },
        ricompensa_2_anni:       { duration: { years: 2 },  label: "Ricompensa 2 anni" },
        ricompensa_5_anni:       { duration: { years: 5 },  label: "Ricompensa 5 anni" },
      };
      Object.keys(rewards).forEach((k) => {
        const r = rewardPeriods[k];
        if (r && rewards[k]) {
          const dt = add(startDateForGoals, r.duration);
          const idx = upcomingGoals.findIndex((g) => format(g.date, "yyyy-MM-dd") === format(dt, "yyyy-MM-dd"));
          if (idx !== -1) {
            upcomingGoals[idx] = {
              ...upcomingGoals[idx],
              label: `${upcomingGoals[idx].label} / ${r.label}`,
              reward: rewards[k],
            };
          } else {
            upcomingGoals.push({ date: dt, label: r.label, reward: rewards[k] });
          }
        }
      });
    } catch {}
  }

  upcomingGoals = upcomingGoals.filter((g) => g.date > today).sort((a, b) => a.date - b.date);

  return { achievements: Array.from(achievements), upcomingGoals, streakDays: Math.max(0, ...sorted.map((_, i) => i)) ? streak : 0, maxStreak };
}

// ——— API principale: calcola + salva su R2
export async function generateAndSaveProgressSnapshot() {
  const u = await getCurrentUserSafe();

  // ⚠️ Non scrivere mai in anon
  if (!u?.email && !u?.id && !u?.uid) {
    throw new Error("Nessun utente autenticato: evito salvataggi anonimi.");
  }

  // check-in: prova email → fallback id/uid
  let allCheckIns = [];
  try { if (u.email) allCheckIns = await DailyCheckIn.filter({ created_by: u.email }, "-date"); } catch {}
  if (!Array.isArray(allCheckIns) || allCheckIns.length === 0) {
    try {
      const who = u.id || u.uid;
      if (who) allCheckIns = await DailyCheckIn.filter({ created_by: who }, "-date");
    } catch {}
  }
  const progress = await UserProgress.filter({ created_by: u.email }).catch(() => []);

  const normalized = (Array.isArray(allCheckIns) ? allCheckIns : []).map(normCheckIn).filter(Boolean);

  const { cigarettesAvoided, totalSavings } = computeSavings(u, normalized);
  const { achievements, upcomingGoals, streakDays } = computeMilestones(normalized, Array.isArray(progress) ? progress : []);

  const snapshot = {
    lesson: "progress_snapshot",
    now: new Date().toISOString(),
    user: { id: u?.id || u?.uid || null, email: u?.email || null, name: u?.full_name || u?.name || null },
    totals: {
      savings_eur: totalSavings,
      cigarettes_avoided: cigarettesAvoided,
    },
    streak_days: streakDays,
    upcoming_goals: upcomingGoals.map((g) => ({
      date: format(g.date, "yyyy-MM-dd"),
      label: g.label,
      ...(g.reward ? { reward: g.reward } : {}),
    })),
    achievements,
  };

  // Salva su R2 — forziamo SEMPRE l’utente nel payload
  await saveToR2("progress_snapshot", {
    data: snapshot,
    meta: { source: "auto_after_checkin" },
    user: { id: u?.id || u?.uid || null, email: u?.email || null },
  });

  try { window.dispatchEvent(new CustomEvent("rb:progress-snapshot-saved", { detail: snapshot })); } catch {}

  return snapshot;
}

/* ===================== PATCH MINIMA PER RETRO-CHECKIN ===================== */
function computeOneDayForRetro(user, dayISO, status, relapseCigsRaw) {
  const yearsPassed = 0;
  const inflate = (x) => x * Math.pow(1.03, yearsPassed);

  let dailyCigEquivalent = 0;
  let dailyCost = 0;

  if (user?.smoking_type === "cigarettes") {
    const cpd = Number(user.cigarettes_per_day ?? user.baseline_cigs_per_day ?? 0);
    const packPriceBase = Number(user.pack_price ?? user.pack_price_eur ?? 0);
    const cigsPerPack = Number(user.cigarettes_per_pack ?? user.cigs_per_pack ?? 20);
    dailyCigEquivalent = cpd;
    const currentPackPrice = inflate(packPriceBase);
    const pricePerCig = cigsPerPack > 0 ? currentPackPrice / cigsPerPack : 0;
    dailyCost = dailyCigEquivalent * pricePerCig;
  } else if (user?.smoking_type === "tobacco") {
    const CIGS_PER_TOBACCO_PACK = 100;
    const weeklyTobaccoPacks = Number(user.tobacco_packs_per_week ?? 0);
    const weeklyPaperPacks   = Number(user.paper_packs_per_week ?? 0);
    const weeklyFilterPacks  = Number(user.filter_packs_per_week ?? 0);

    const tobaccoPackPriceBase = Number(user.tobacco_pack_price ?? 0);
    const paperPackPriceBase   = Number(user.paper_pack_price ?? 0);
    const filterPackPriceBase  = Number(user.filter_pack_price ?? 0);

    dailyCigEquivalent = (weeklyTobaccoPacks * CIGS_PER_TOBACCO_PACK) / 7;

    const dailyTobaccoCost = (weeklyTobaccoPacks * inflate(tobaccoPackPriceBase)) / 7;
    const dailyPaperCost   = (weeklyPaperPacks   * inflate(paperPackPriceBase))   / 7;
    const dailyFilterCost  = (weeklyFilterPacks  * inflate(filterPackPriceBase))  / 7;

    dailyCost = dailyTobaccoCost + dailyPaperCost + dailyFilterCost;
  }

  const weeklyLighters = Number(user?.lighters_per_week ?? 0);
  dailyCost += (weeklyLighters * inflate(1)) / 7;

  let relapseCigs = Number(relapseCigsRaw ?? 0);
  if (!Number.isFinite(relapseCigs) || relapseCigs < 0) relapseCigs = 0;
  if (dailyCigEquivalent > 0) relapseCigs = Math.min(relapseCigs, dailyCigEquivalent);

  const pricePerCigEq = dailyCigEquivalent > 0 ? dailyCost / dailyCigEquivalent : 0;
  const costRelapse = relapseCigs * pricePerCigEq;

  const cigarettes_avoided = Math.max(0, Math.round(dailyCigEquivalent - relapseCigs));
  const savings_eur = Math.max(0, Math.round((dailyCost - costRelapse) * 100) / 100);

  return { cigarettes_avoided, savings_eur };
}

// === SALVATAGGIO CHECK-IN RETRODATATO (salva evento + aggiorna stats del giorno)
export async function saveBackdatedCheckin({
  date,                                // "YYYY-MM-DD" (obbligatorio)
  status = "success",                  // "success" | "relapse"
  relapse_cigarettes_smoked = 0,       // numero (se relapse)
  cigarettes_avoided,                  // se assente/0 → calcoliamo noi
  savings_eur,                         // se assente/0 → calcoliamo noi
  note = "",                           // opzionale
  source = "retro_checkin",            // tag sorgente per debug
}) {
  const day = String(date || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    throw new Error("saveBackdatedCheckin: 'date' deve essere YYYY-MM-DD");
  }

  const u = await getCurrentUserSafe();

  // ⚠️ Evita salvataggi anonimi
  if (!u?.email && !u?.id && !u?.uid) {
    throw new Error("Nessun utente autenticato: evito salvataggi anonimi (retro).");
  }

  const created_by = u?.email || u?.id || u?.uid || "anon";
  const created_at = new Date(`${day}T12:00:00.000Z`).toISOString();

  // Se UI non passa i numeri (o li passa a 0), calcolali qui
  if (!Number(cigarettes_avoided) || !Number(savings_eur)) {
    const calc = computeOneDayForRetro(u, day, status, relapse_cigarettes_smoked);
    if (!Number(cigarettes_avoided)) cigarettes_avoided = calc.cigarettes_avoided;
    if (!Number(savings_eur))        savings_eur        = calc.savings_eur;
  }

  // record coerente con CheckIn.jsx (alias inclusi per compatibilità)
  const record = {
    date: day,
    created_by,
    created_at,
    status: status === "relapse" ? "relapse" : "success",
    q3_breathing: status === "relapse" ? "relapse" : "success",
    relapse_cigarettes_smoked: Number(relapse_cigarettes_smoked || 0),
    cigarettes_avoided: Number(cigarettes_avoided || 0),
    cigs_avoided: Number(cigarettes_avoided || 0),
    savings_eur: Number(savings_eur || 0),
    eur: Number(savings_eur || 0),
    note,
  };

  // 1) Persisti localmente lo storico (se usi l’Entity client-side)
  try { await DailyCheckIn.create(record); } catch {}

  // 2) Salva l’evento grezzo in R2 (cartella checkins) — forziamo SEMPRE l’utente
  await saveToR2("daily_checkin", {
    data: {
      ...record,
      user_agent: (typeof navigator !== "undefined" ? navigator.userAgent : ""),
    },
    meta: { source, override_day: day },
    user: { id: u?.id || u?.uid || null, email: u?.email || null },
  });

  // 3a) Stat del GIORNO (file datato)
  const dayStats = {
    date: day,
    relapses: record.status === "relapse" ? 1 : 0,
    relapse_cigarettes_smoked: record.relapse_cigarettes_smoked,
    cigarettes_avoided: record.cigarettes_avoided,
    saved_today: record.savings_eur,
    lastCheckin: day,
    achieved_goals: [],
    next_goals: [],
  };
  await saveToR2("user_stats_progress", {
    data: dayStats,
    meta: { source, override_day: day },
    user: { id: u?.id || u?.uid || null, email: u?.email || null },
  });

  // 3b) Aggiorna anche il latest.json
  await saveToR2("user_stats_progress", {
    data: dayStats,
    meta: { source },
    user: { id: u?.id || u?.uid || null, email: u?.email || null },
  });

  // 4) Notifica la UI per refresh immediato
  try { window.dispatchEvent(new Event("rb:checkin-saved")); } catch {}

  return { ok: true, day, created_at };
}

// === PATCH FINALE: retro + sync cache + snapshot ===
export async function saveBackdatedCheckinAndRefresh({
  date,
  status = "success",
  relapse_cigarettes_smoked = 0,
  cigarettes_avoided,
  savings_eur,
  note = "",
  source = "retro_checkin",
}) {
  // scrive su R2 (checkin + stats + latest) e lancia rb:checkin-saved
  const out = await saveBackdatedCheckin({
    date,
    status,
    relapse_cigarettes_smoked,
    cigarettes_avoided,
    savings_eur,
    note,
    source,
  });

  // allinea anche la cache locale RB_DailyCheckIn per eventuali UI che la leggono
  try {
    const u = await getCurrentUserSafe();
    const day = String(date || "").slice(0, 10);
    const calc = computeOneDayForRetro(u, day, status, relapse_cigarettes_smoked);
    const cigs = Number(cigarettes_avoided || calc.cigarettes_avoided || 0);
    const eur  = Number(savings_eur || calc.savings_eur || 0);

    const arr = JSON.parse(localStorage.getItem("RB_DailyCheckIn") || "[]");
    const idx = arr.findIndex(x => x?.date === day);
    const base = {
      date: day,
      created_by: u?.email || u?.id || u?.uid || "anon",
      created_at: new Date(`${day}T12:00:00.000Z`).toISOString(),
      status: status === "relapse" ? "relapse" : "success",
      q3_breathing: status === "relapse" ? "relapse" : "success",
      relapse_cigarettes_smoked: Number(relapse_cigarettes_smoked || 0),
      cigarettes_avoided: cigs,
      cigs_avoided: cigs,
      savings_eur: eur,
      eur,
      note,
    };
    if (idx >= 0) arr[idx] = { ...arr[idx], ...base };
    else arr.push(base);
    localStorage.setItem("RB_DailyCheckIn", JSON.stringify(arr));
  } catch {}

  // rigenera snapshot totale (carte/goal) e notifica UI
  try { await generateAndSaveProgressSnapshot(); } catch {}
  try { window.dispatchEvent(new Event("rb:checkin-saved")); } catch {}

  return out;
}
